// Aim assist (§8.5) and the AimContext the input layer samples against. Pure: no DOM.
import { NO_SLOT, TUNE } from '@shared/constants';
import { EK } from '@shared/ids';
import { angleBetween, DEG, setV } from '@shared/math';
import type { Vec2 } from '@shared/types';
import type { FrameView } from '../game/FrameView';
import type { Settings } from '../profile/settings';
import type { AimContext, InputDevice } from './InputManager';

export interface AssistCone { halfAngle: number; range: number }

type AssistTarget = AimContext['targets'][number];

/** The cone for a Shove or Grab on `device`, or null when the setting is Off (§8.5). */
export function assistCone(device: InputDevice, setting: Settings['gameplay']['aimAssist'], grab: boolean): AssistCone | null {
  if (setting === 'off') return null;
  const a = TUNE.input.assist;
  const spec = a[device];
  const mult = setting === 'low' ? a.lowConeMult : 1;
  return { halfAngle: spec.coneDeg * DEG * mult, range: grab ? a.grabRange : spec.range };
}

/**
 * Writes the unit direction to the best target inside the cone around `facing` into `out`: the
 * one minimizing `distance × (1 + angle / cone)`. Teammates are skipped. Returns false (and
 * writes (0, 0), meaning "use facing") when no target qualifies.
 */
export function bestAssistDir(selfX: number, selfY: number, facing: number, targets: readonly AssistTarget[], cone: AssistCone, out: Vec2): boolean {
  const fx = Math.cos(facing);
  const fy = Math.sin(facing);
  let best = Infinity;
  let bx = 0;
  let by = 0;
  for (const t of targets) {
    if (t.isTeammate) continue;
    const dx = t.x - selfX;
    const dy = t.y - selfY;
    const d = Math.hypot(dx, dy);
    if (d === 0 || d > cone.range) continue;
    const angle = angleBetween(fx, fy, dx, dy);
    if (angle > cone.halfAngle) continue;
    const score = d * (1 + angle / cone.halfAngle);
    if (score < best) {
      best = score;
      bx = dx / d;
      by = dy / d;
    }
  }
  setV(out, bx, by);
  return best < Infinity;
}

/**
 * Touch throw aim (§8.3): the nearest enemy within `TUNE.input.touchThrowConeDeg` of facing (a
 * half-angle, like the §8.5 assist cones) and `touchThrowRange` m. Writes the unit direction, or
 * (0, 0) for "use facing", and returns whether a target was found.
 */
export function touchThrowDir(selfX: number, selfY: number, facing: number, targets: readonly AssistTarget[], out: Vec2): boolean {
  const half = TUNE.input.touchThrowConeDeg * DEG;
  const range = TUNE.input.touchThrowRange;
  const fx = Math.cos(facing);
  const fy = Math.sin(facing);
  let best = Infinity;
  let bx = 0;
  let by = 0;
  for (const t of targets) {
    if (t.isTeammate) continue;
    const dx = t.x - selfX;
    const dy = t.y - selfY;
    const d = Math.hypot(dx, dy);
    if (d === 0 || d > range || d >= best) continue;
    if (angleBetween(fx, fy, dx, dy) > half) continue;
    best = d;
    bx = dx / d;
    by = dy / d;
  }
  setV(out, bx, by);
  return best < Infinity;
}

export function createAimContext(groundFromScreen: AimContext['groundFromScreen']): AimContext {
  return { selfX: 0, selfY: 0, facing: 0, groundFromScreen, targets: [] };
}

/**
 * Refreshes the aim context from the frame being shown: the local bean, and every other
 * non-ghost bean and dummy at its rendered position. Target records are reused.
 */
export function syncAimContext(ctx: AimContext, view: FrameView): void {
  const self = view.localSlot !== NO_SLOT ? view.beans[view.localSlot] : undefined;
  if (self) {
    ctx.selfX = self.x;
    ctx.selfY = self.y;
    ctx.facing = self.facing;
  }
  const targets = ctx.targets;
  let n = 0;
  const push = (slot: number, x: number, y: number, isTeammate: boolean): void => {
    const t = targets[n] ?? (targets[n] = { slot, x, y, isTeammate });
    t.slot = slot;
    t.x = x;
    t.y = y;
    t.isTeammate = isTeammate;
    n++;
  };
  for (const b of view.beans) {
    if (!b.active || b.out || b.ghost || b.slot === view.localSlot) continue;
    push(b.slot, b.x, b.y, self !== undefined && self.team !== 0 && b.team === self.team);
  }
  for (const e of view.ents) if (e.kind === EK.DUMMY) push(NO_SLOT, e.x, e.y, false);
  targets.length = n;
}
